import TableStyle from "@components/TableStyle";
import MaterialTable from "@material-table/core";
import {useState, useContext, useRef, useEffect, forwardRef, useMemo} from "react";
import {useIntl} from "react-intl";
import CountryContext from "CountryContext";
import {getColumns} from "./getColumns";
import apiRequest from "@services/apiRequest";
import {createFilterParams, createOrderingParams} from "@components/Tables/utils";
import useCountryAxiosInstance from "@hooks/useCountryAxiosInstance";
import Modal from "@ui-components/Modal";
import {GameWorksitesTableProps, Option, UsersInvolved} from "./types";


export const WorksitesTable = ({
  visibility_user_ids,
  country,
  start_date,
  end_date,
  role,
  ambiti,
  ownership,
  statuses,
  worksite_types,
  services,
  specified,
  specified_statuses,
  selectedAreaManagers,
  players
}: GameWorksitesTableProps) => {
  const intl = useIntl();
  const {
    country: {code},
  } = useContext(CountryContext)
  const countryApiRequest = useCountryAxiosInstance()


  const [userMap, setUserMap] = useState<{[key: number]: string}>();
  const [allOptions, setAllOptions] = useState<Option[]>();
  const [userOptions, setUserOptions] = useState<Option[]>();
  const [servicesMap, setServicesMap] = useState<{[key: number]: string}>();
  const [showInvolvedUsersModal, setShowInvolvedUsersModal] = useState<boolean | UsersInvolved>(false);

  const ref = useRef(null);

  useEffect(() => {
    const fetchData =  async () => {
      // we need to fetch all internal users in users kpi table in order to display
      // creators sharers and viewers in the table, and in order to let the user pick
      // them to filter the table
      const [users, svs] = await Promise.all([
        apiRequest.get(`/users_kpi/usernames/${code}`),
        countryApiRequest.get("/services/all")
      ]);
      setUserMap(users.reduce((acc, u) => {
        acc[u.id] = u.fullname
        return acc
      }, {}));
      setAllOptions(users.map((u) => ({label: u.fullname, value: u.id})));
      setUserOptions(users.map((u) => ({label: u.fullname, value: u.id})));
      setServicesMap(svs.reduce((acc, s) => {
        acc[s.id] = s.name
        return acc
      }, {}));
    }

    fetchData();
  }, [country])

  const [columns, setColumns] = useState<any | undefined>();

  useEffect(() => {
    if (userMap && servicesMap && userOptions) {
      const cols = getColumns(
        intl,
        code,
        userMap,
        userOptions,
        servicesMap,
        setShowInvolvedUsersModal
      );
      setColumns(cols);
    }
  }, [userMap, servicesMap, userOptions]);

  // Create a stable key from all filter dependencies to avoid unnecessary re-renders
  // This prevents the effect from firing when arrays/objects are recreated with the same content
  const filterKey = useMemo(() => {
    return JSON.stringify({
      visibility_user_ids,
      country,
      start_date,
      end_date,
      role,
      ambiti,
      ownership,
      statuses,
      worksite_types,
      services,
      specified,
      specified_statuses,
      selectedAreaManagers,
      players
    });
  }, [
    visibility_user_ids,
    country,
    start_date,
    end_date,
    role,
    ambiti,
    ownership,
    statuses,
    worksite_types,
    services,
    specified,
    specified_statuses,
    selectedAreaManagers,
    players
  ]);

  useEffect(() => {
    const refetch = async () => {
      const usersInvolved = await apiRequest.post("analytics/game/table/users_involved", {
          visibility_user_ids,
          country,
          start_date,
          end_date,
          role,
          ambiti,
          ...(ownership ? {ownership} : {}),
          ...(statuses ? {statuses} : {}),
          ...(worksite_types ? {worksite_types} : {}),
          ...(services ? {services} : {}),
          ...(specified ? {specified: true, specified_statuses: specified_statuses} : {}),
          ...(selectedAreaManagers && selectedAreaManagers.length > 0 ? {area_manager_ids: selectedAreaManagers} : {}),
          ...(players && players.length > 0 ? {players: players} : {})
        });
      if (usersInvolved) {
        setUserOptions(allOptions?.filter((o) => usersInvolved.includes(o.value)));
      }
      if (ref.current) {
        ref.current.state.query.page = 0;
        ref.current.state.query.totalCount = 0;
        ref.current.onQueryChange({
          filters:[]
        });
      }
    }
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  return <>
    <TableStyle>
      <MaterialTable
        tableRef={ref}
        options={{
          filtering: true,
          toolbar: false,
          sorting: true,
          pageSizeOptions: [5, 10, 20],
          headerStyle: {
            fontWeight: "bold",
            fontSize: 15,
            whitespace: "nowrap",
          },
          cellStyle: {
            textTransform: "uppercase",
          }
        }}
        data={async (query) => {
          const ordering = createOrderingParams(query);
          const payload = {
            offset: query.page * query.pageSize,
            limit: query.pageSize,
            visibility_user_ids,
            country,
            start_date,
            end_date,
            role,
            ambiti,
            ...(ownership ? {ownership} : {}),
            ...(statuses ? {statuses} : {}),
            ...(worksite_types ? {worksite_types} : {}),
            ...(services ? {services} : {}),
            ...(specified ? {specified:true, specified_statuses:specified_statuses} : {}),
            ...(ordering && {ordering}),
            ...(selectedAreaManagers && selectedAreaManagers.length > 0 ? {area_manager_ids: selectedAreaManagers} : {}),
            ...(players && players.length > 0 ? {players: players} : {})
          }
          // adding table filters
          if (query.filters?.length > 0) {
            for (const filter of query.filters) {
              // filter will only be  list of int (user ids) or a string (name / address of worksite)
              if (filter.value && filter.value.length > 0)
                payload[filter.column.field] = filter.value;
            }
          }
          const {
            items, total
          } = await apiRequest.post(
            "analytics/game/table/worksites",
            payload,
          )
          return {
            data: items,
            page: query.page,
            totalCount: total,
          }
        }}
        columns={columns}
      />
    </TableStyle>
    {showInvolvedUsersModal &&
      <Modal
        onExit={() => setShowInvolvedUsersModal(false)}
      >
        <div className={'py-4 justify-items-center items-center'}>
          <div className={'grid grid-cols-1 gap-4'}>
            {
              showInvolvedUsersModal.created &&
              <div>
                {intl.formatMessage({id: "created_by", defaultMessage: "creato da"})}{': '}
                <ul className={"list-disc ml-5"}><li>{userMap[showInvolvedUsersModal.created]}</li></ul>
              </div>
            }
            {
              showInvolvedUsersModal.shared?.length > 0 &&
              <div>
                {intl.formatMessage({
                  id: "shared_to",
                  defaultMessage: "condiviso a"
                })}{': '}
                <ul className={"list-disc ml-5"}>{showInvolvedUsersModal.shared.map((s) => <li key={s}>{userMap[s]}</li>)}</ul>
              </div>
            }
            {
              showInvolvedUsersModal.visible?.length > 0 &&
              <div>
                {intl.formatMessage({
                  id: "visible_to",
                  defaultMessage: "visibile a"
                })}{': '}
                <ul className={"list-disc ml-5"}>{showInvolvedUsersModal.visible.map((s) => <li key={s}>{userMap[s]}</li>)}</ul>
              </div>
            }
          </div>
        </div>
      </Modal>}
  </>

}